import React, { FC, useState, useEffect } from "react";
import { Card, Table, Button, Input, Select, Space, Tag, Dropdown, Menu, notification, Modal, Row, Col, Descriptions, Divider } from "antd";
import { 
  FileTextOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  HomeOutlined,
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  MoreOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CaseRecordProps } from '../utils/types';
import { getCases, promoteCase, getAuthToken } from '../utils/functions';
import { BaseUrl, CaseDetailsUrl } from '../utils/network';
import { useGetCases } from '../utils/hooks';
import axios from 'axios';

const { Option } = Select;

interface CaseProps {
  id: number;
  name: string;
  company: string;
  caseType: string;
  discriminationBasis: string;
  status: string;
  createdAt: string;
}

const CaseManagement: FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCaseType, setSelectedCaseType] = useState<string>('all');

  // Real data from API using hooks
  const [allCases, setAllCases] = useState<CaseRecordProps[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Modal state for case details
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseRecordProps | null>(null);
  
  // Delete modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState<CaseRecordProps | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Check if user is admin
  const userRole = localStorage.getItem('token1'); // token1 is the role key
  const isAdmin = userRole?.toLowerCase() === 'admin';

  // Use custom hook to fetch cases
  useGetCases(setAllCases, setLoading);

  // Manual refresh function
  const fetchCases = () => {
    getCases(setAllCases, setLoading);
  };

  
  // Handle form actions
  const handleCreateCase = () => {
    navigate('/create-case');
  };



  const handleFormSuccess = () => {
    fetchCases(); // Refresh the list after successful form submission
  };

  const handleViewCase = (caseRecord: CaseRecordProps) => {
    setSelectedCase(caseRecord);
    setIsDetailModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalVisible(false);
    setSelectedCase(null);
  };

  const handleDeleteClick = (caseRecord: CaseRecordProps) => {
    setCaseToDelete(caseRecord);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!caseToDelete) return;
    
    setDeleting(true);
    try {
      const headers = getAuthToken();
      if (!headers) {
        throw new Error('Authentication required');
      }
      await axios.delete(`${CaseDetailsUrl}${caseToDelete.id}/`, headers);
      notification.success({
        message: 'Interview Deleted',
        description: `Case #${caseToDelete.case_no} has been deleted successfully.`
      });
      setDeleteModalVisible(false);
      setCaseToDelete(null);
      fetchCases(); // Refresh the list
    } catch (error) {
      console.error('Error deleting case:', error);
      notification.error({
        message: 'Delete Failed',
        description: 'Failed to delete the interview. Please try again.'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setCaseToDelete(null);
  };

  const handlePromoteCase = async (caseRecord: CaseRecordProps, prevailValue: string) => {
    try {
      await promoteCase(caseRecord.id!, prevailValue);
      notification.success({
        message: "Case Promoted",
        description: `Case ${caseRecord.case_no} has been promoted to InfoForLetter stage.`,
      });
      fetchCases(); // Refresh the list
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to promote case. Please try again.",
      });
    }
  };


  const filteredCases = allCases.filter(case_ => {
    const complainantName = [case_.first_name, case_.middle, case_.last_name].filter(Boolean).join(' ');
    const caseType = case_.case_type || case_.type || '';
    const basis = case_.discrimination_basis || case_.basis || '';
    
    const matchesSearch = searchTerm === '' ||
                         case_.case_no?.toString().includes(searchTerm) ||
                         complainantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         case_.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         basis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         caseType?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedCaseType === 'all' || caseType === selectedCaseType;
    return matchesSearch && matchesType;
  });

  // Table columns
  const columns = [
    {
      title: 'Case Number',
      dataIndex: 'case_no',
      key: 'case_no',
      width: 120,
      render: (caseNo: number) => (
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: '#1890ff',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '13px',
          fontWeight: 600,
          margin: '0 auto'
        }}>
          {caseNo}
        </div>
      )
    },
    {
      title: 'Complainant',
      key: 'complainant',
      width: 200,
      render: (record: CaseRecordProps) => (
        <span>{[record.first_name, record.middle, record.last_name].filter(Boolean).join(' ') || 'N/A'}</span>
      )
    },
    {
      title: 'Accused',
      key: 'accused',
      width: 200,
      render: (record: CaseRecordProps) => {
        const accusedName = [record.accused_first_name, record.accused_last_name].filter(Boolean).join(' ');
        return <span>{accusedName || 'N/A'}</span>;
      }
    },
    {
      title: 'Case Type',
      key: 'case_type',
      width: 180,
      render: (record: CaseRecordProps) => (
        <Tag color="blue">{record.case_type || record.type || 'N/A'}</Tag>
      )
    },
    {
      title: 'Case Basis',
      key: 'discrimination_basis',
      width: 180,
      render: (record: CaseRecordProps) => (
        <Tag color="purple">{record.discrimination_basis || record.basis || 'N/A'}</Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: isAdmin ? 150 : 80,
      fixed: 'right' as const,
      render: (record: CaseRecordProps) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/create-case-wizard?caseId=${record.id}`)}
          >
            View
          </Button>
          {isAdmin && (
            <Button
              danger
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(record);
              }}
            >
              Delete
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Header Card */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ padding: '16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <span style={{ fontSize: '24px', fontWeight: 600, color: '#000' }}>View Interviews</span>
            </div>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={fetchCases}
                loading={loading}
                type="default"
              >
                Refresh
              </Button>
              <Button 
                icon={<HomeOutlined />} 
                onClick={() => navigate('/')}
                type="default"
              >
                Home
              </Button>
            </Space>
          </div>
        </div>
      </Card>


      {/* Search & Filter */}
      <Card style={{ 
        marginBottom: '16px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none'
      }}>
        <div style={{ color: 'white', fontWeight: 600, fontSize: '16px', marginBottom: '16px' }}>
          Search & Filter
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px auto auto', gap: '16px', alignItems: 'end' }}>
          <div>
            <div style={{ color: 'white', marginBottom: '8px', fontSize: '14px' }}>Search</div>
            <Input
              placeholder="Name, company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </div>
          <div>
            <div style={{ color: 'white', marginBottom: '8px', fontSize: '14px' }}>Case Type</div>
            <Select
              value={selectedCaseType}
              onChange={setSelectedCaseType}
              style={{ width: '100%' }}
              placeholder="All Types"
            >
              <Option value="all">All Types</Option>
              <Option value="Different Treatment">Different Treatment</Option>
              <Option value="Harassment">Harassment</Option>
              <Option value="Retaliation">Retaliation</Option>
              <Option value="Reasonable Accommodation">Reasonable Accommodation</Option>
              <Option value="Equal Pay">Equal Pay</Option>
              <Option value="Other">Other</Option>
            </Select>
          </div>
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.3)' }}
          >
            Search
          </Button>
          <Button 
            type="default" 
            style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
          >
            Clear
          </Button>
        </div>
      </Card>

       {/* Main Content - Table */}
       <Card style={{ marginBottom: 16 }}>
         <Table
           columns={columns}
           dataSource={filteredCases}
           rowKey="id"
           loading={loading}
           pagination={{
             pageSize: 10,
             showSizeChanger: true,
             showTotal: (total) => `Total ${total} interviews`
           }}
           scroll={{ x: 1000 }}
           locale={{
             emptyText: (
               <div style={{ textAlign: 'center', padding: '40px' }}>
                 <FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                 <div style={{ fontSize: '16px', color: '#8c8c8c', marginBottom: '8px' }}>
                   No interviews found
                 </div>
                 <div style={{ fontSize: '14px', color: '#bfbfbf' }}>
                   Try adjusting your search criteria
                 </div>
             </div>
             )
           }}
         />
           </Card>


      {/* Case Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <span>Case Details - #{selectedCase?.case_no}</span>
          </div>
        }
        open={isDetailModalVisible}
        onCancel={handleCloseModal}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseModal}>
            Close
          </Button>
        ]}
      >
        {selectedCase && (
          <div>
            {/* Basic Information */}
            <Card 
              size="small"
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileTextOutlined />
                  <span>Basic Information</span>
                </div>
              }
              style={{ marginBottom: 12 }}
            >
              <Row gutter={12}>
                <Col span={6}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: 4 }}>Case #</div>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#1890ff',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                      margin: '0 auto'
                    }}>
                      {selectedCase.case_no}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: 4 }}>Discrimination Basis</div>
                  <Tag color="blue" style={{ fontSize: '11px', padding: '2px 8px', margin: 0 }}>
                    {selectedCase.discrimination_basis || selectedCase.basis || 'N/A'}
                  </Tag>
                </Col>
                <Col span={6}>
                  <div style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: 4 }}>Likelihood</div>
                  <Tag 
                    color={selectedCase.likelihood === 'High' ? 'success' : selectedCase.likelihood === 'Medium' ? 'warning' : 'default'}
                    style={{ fontSize: '11px', padding: '2px 8px', margin: 0, fontWeight: 600 }}
                  >
                    {selectedCase.likelihood || 'N/A'}
                  </Tag>
                </Col>
                <Col span={6}>
                  <div style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: 4 }}>Case Type</div>
                  <div style={{ fontSize: '12px', fontWeight: 500 }}>{selectedCase.case_type || selectedCase.type || 'N/A'}</div>
                </Col>
              </Row>
              
              <Row gutter={12} style={{ marginTop: 8 }}>
                <Col span={8}>
                  <div style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: 4 }}>Complainant</div>
                  <div style={{ fontSize: '12px', fontWeight: 500 }}>
                    {[selectedCase.first_name, selectedCase.middle, selectedCase.last_name].filter(Boolean).join(' ') || 'N/A'}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: 4 }}>Accused</div>
                  <div style={{ fontSize: '12px', fontWeight: 500 }}>
                    {[selectedCase.accused_first_name, selectedCase.accused_last_name].filter(Boolean).join(' ') || 'N/A'}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: 4 }}>Created By</div>
                  <div style={{ fontSize: '12px', fontWeight: 500 }}>{selectedCase.created_by_name || 'Unknown'}</div>
                </Col>
              </Row>
            </Card>

            {/* Case Details */}
            <Card 
              size="small"
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <EditOutlined />
                  <span>Case Details</span>
                </div>
              }
              style={{ marginBottom: 12 }}
            >
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Company Name" span={1}>
                  <div style={{ fontSize: '12px' }}>{selectedCase.company_name || 'Not provided'}</div>
                </Descriptions.Item>
                <Descriptions.Item label="Date Discrimination Occurred" span={1}>
                  <div style={{ fontSize: '12px' }}>
                    {selectedCase.date_discrimination_occurred ? new Date(selectedCase.date_discrimination_occurred).toLocaleDateString() : 'Not provided'}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Date of Interview" span={1}>
                  <div style={{ fontSize: '12px' }}>
                    {selectedCase.date_of_interview ? new Date(selectedCase.date_of_interview).toLocaleDateString() : 'Not provided'}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Weighted Score" span={1}>
                  <div style={{ fontSize: '12px', fontWeight: 500 }}>
                    {selectedCase.weighted_score !== undefined && selectedCase.weighted_score !== null 
                      ? (() => {
                          const score = typeof selectedCase.weighted_score === 'number' 
                            ? selectedCase.weighted_score 
                            : parseFloat(String(selectedCase.weighted_score));
                          return !isNaN(score) ? score.toFixed(2) : 'N/A';
                        })()
                      : 'N/A'}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Basis-Specific Information */}
            {(selectedCase.your_race || selectedCase.your_sex || selectedCase.your_age || selectedCase.your_religion || 
              selectedCase.your_color || selectedCase.your_national_origin || selectedCase.your_handicap || selectedCase.your_reprisal) && (
              <Card 
                size="small"
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <UserOutlined />
                    <span>Complainant Basis Information</span>
                  </div>
                }
                style={{ marginBottom: 12 }}
              >
                <Descriptions bordered column={2} size="small">
                  {selectedCase.your_race && (
                    <Descriptions.Item label="Race" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.your_race}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.your_sex && (
                    <Descriptions.Item label="Sex" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.your_sex}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.your_age && (
                    <Descriptions.Item label="Age" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.your_age}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.your_religion && (
                    <Descriptions.Item label="Religion" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.your_religion}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.your_color && (
                    <Descriptions.Item label="Color" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.your_color}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.your_national_origin && (
                    <Descriptions.Item label="National Origin" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.your_national_origin}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.your_handicap && (
                    <Descriptions.Item label="Handicap" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.your_handicap}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.your_reprisal && (
                    <Descriptions.Item label="Reprisal" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.your_reprisal}</div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {/* Accused Basis Information */}
            {(selectedCase.accused_race || selectedCase.accused_sex || selectedCase.accused_age || selectedCase.accused_religion || 
              selectedCase.accused_color || selectedCase.accused_national_origin || selectedCase.accused_handicap || selectedCase.accused_reprisal) && (
              <Card 
                size="small"
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <UserOutlined />
                    <span>Accused Basis Information</span>
                  </div>
                }
                style={{ marginBottom: 12 }}
              >
                <Descriptions bordered column={2} size="small">
                  {selectedCase.accused_race && (
                    <Descriptions.Item label="Race" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.accused_race}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.accused_sex && (
                    <Descriptions.Item label="Sex" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.accused_sex}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.accused_age && (
                    <Descriptions.Item label="Age" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.accused_age}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.accused_religion && (
                    <Descriptions.Item label="Religion" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.accused_religion}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.accused_color && (
                    <Descriptions.Item label="Color" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.accused_color}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.accused_national_origin && (
                    <Descriptions.Item label="National Origin" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.accused_national_origin}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.accused_handicap && (
                    <Descriptions.Item label="Handicap" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.accused_handicap}</div>
                    </Descriptions.Item>
                  )}
                  {selectedCase.accused_reprisal && (
                    <Descriptions.Item label="Reprisal" span={1}>
                      <div style={{ fontSize: '12px' }}>{selectedCase.accused_reprisal}</div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}

            {/* Metadata */}
            <Card 
              size="small"
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircleOutlined />
                  <span>Metadata</span>
                </div>
              }
            >
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Created At" span={1}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '12px' }}>
                    <CalendarOutlined style={{ color: '#52c41a' }} />
                    {selectedCase.created_at ? new Date(selectedCase.created_at).toLocaleDateString() : 'N/A'}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Last Updated" span={1}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '12px' }}>
                    <CalendarOutlined style={{ color: '#faad14' }} />
                    {selectedCase.updated_at ? new Date(selectedCase.updated_at).toLocaleDateString() : 'N/A'}
                  </div>
                </Descriptions.Item>
                {selectedCase.pdf_url && (
                  <Descriptions.Item label="PDF Report" span={2}>
                    <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <a 
                        href={(() => {
                          // Construct full URL from relative path
                          const pdfUrl = selectedCase.pdf_url || '';
                          if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) {
                            return pdfUrl; // Already absolute URL
                          }
                          // Extract origin (protocol + host + port) from BaseUrl
                          try {
                            const baseUrlObj = new URL(BaseUrl);
                            const origin = `${baseUrlObj.protocol}//${baseUrlObj.host}`;
                            // Ensure pdfUrl starts with /
                            const cleanPdfUrl = pdfUrl.startsWith('/') ? pdfUrl : `/${pdfUrl}`;
                            return `${origin}${cleanPdfUrl}`;
                          } catch (e) {
                            // Fallback: if URL parsing fails, try string manipulation
                            const baseUrl = BaseUrl.replace('/api/', '').replace(/\/$/, '');
                            const cleanPdfUrl = pdfUrl.startsWith('/') ? pdfUrl : `/${pdfUrl}`;
                            return `${baseUrl}${cleanPdfUrl}`;
                          }
                        })()}
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 4,
                          color: '#1890ff',
                          textDecoration: 'none'
                        }}
                        download={selectedCase.pdf_filename || 'case_report.pdf'}
                      >
                        <DownloadOutlined />
                        {selectedCase.pdf_filename || 'Download Report'}
                      </a>
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>
      </Card>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Interview"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
      >
        <p>Are you sure you want to delete Case #{caseToDelete?.case_no}?</p>
        <p style={{ color: '#ff4d4f', fontSize: '12px' }}>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default CaseManagement;
